import React, { useMemo } from "react";
import { Offcanvas, Button, Form, Row, Col } from "react-bootstrap";

const EmployeePayrollOffcanvas = ({
    show,
    onHide,
    employeePayrollData,
    employeeDetails,
    payrollTotals,
    updatePayrollData,
    selectedDeduction,
    setSelectedDeduction,
    deductionAmount,
    setDeductionAmount,
    deductions,
    handleAddDeduction,
    removeDeduction,
    handleSave,
    actionType // 'edit' or 'view'
}) => {
    const isViewMode = actionType === 'view';

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: 650 }}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    {employeeDetails
                        ? `${isViewMode ? 'View' : 'Edit'} Payroll - ${employeeDetails.first_name || ""} ${employeeDetails.last_name || ""}`
                        : "Loading..."}
                </Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body>
                {!employeePayrollData ? (
                    <div className="text-center py-5">Loading payroll...</div>
                ) : (
                    <>
                        {/* Earnings */}
                        <div className="p-3 border-bottom">
                            <h6 className="fw-bold text-uppercase text-primary mb-3">Earnings Breakdown</h6>

                            <div className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                                <span>Basic Salary</span>
                                <span className="fw-bold text-success">
                                    {(employeePayrollData.basicSalary || 0).toFixed(2)}
                                </span>
                            </div>
                            <br />

                            {(employeePayrollData.overtime || []).length > 0 && (
                                <>
                                    <h6 className="fw-semibold text-muted text-uppercase small mb-2">Overtime</h6>
                                    {(employeePayrollData.overtime || []).map((ot, i) => (
                                        <div key={i} className="d-flex justify-content-between align-items-center ps-3 small mb-1">
                                            <span>{ot.date}: {ot.hours}h @  {ot.rate}/h</span>
                                            <span className="text-success">+{((ot.hours || 0) * (ot.rate || 0)).toFixed(2)}</span>
                                        </div>
                                    ))}
                                    <div className="d-flex justify-content-between align-items-center ps-3 mt-2 pt-1 border-top">
                                        <small>Subtotal </small>
                                        <small className="fw-bold text-success"> {payrollTotals.overtimeTotal.toFixed(2)}</small>
                                    </div>
                                </>
                            )}
                            <br />

                            {/* Allowances (read-only) */}
                            {(employeeDetails.contract?.allowances || []).length > 0 && (
                                <>
                                    <h6 className="fw-semibold text-muted text-uppercase small mb-2">Allowances</h6>
                                    {employeeDetails.contract.allowances.map((a, i) => (
                                        <div key={i} className="d-flex justify-content-between align-items-center ps-3 small mb-1">
                                            <span>{a.name}</span>
                                            <span className="text-success">+{(parseFloat(a.pivot?.amount) || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                    <div className="d-flex justify-content-between align-items-center ps-3 mt-2 pt-1 border-top">
                                        <small>Subtotal </small>
                                        <small className="fw-bold text-success"> {payrollTotals.allowancesTotal.toFixed(2)}</small>
                                    </div>
                                </>
                            )}
                            <br />

                            {/* Bonus */}
                            <div className="mb-3">
                                <h6 className="fw-semibold text-muted text-uppercase small mb-2">Bonus</h6>
                                <Form.Control
                                    type="number"
                                    value={employeePayrollData.bonus}
                                    onChange={(e) => updatePayrollData("bonus", parseFloat(e.target.value || 0))}
                                    disabled={isViewMode} // Disable in view mode
                                    readOnly={isViewMode} // Make read-only in view mode
                                />
                            </div>

                            <div className="bg-primary bg-opacity-10 p-3 rounded border">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-bold text-primary">Total Earnings</span>
                                    <span className="fw-bold fs-5 text-primary">
                                        {Number(payrollTotals.grossSalary || 0).toFixed(2)}

                                    </span>
                                </div>
                            </div>
                        </div>
                        <br />

                        {/* Deductions */}
                        <div className="p-3 border-bottom">
                            <h6 className="fw-bold text-uppercase text-danger mb-3">Deductions Breakdown</h6><br />

                            {(employeePayrollData.loans || []).length > 0 && (
                                <>
                                    <h6 className="fw-semibold text-muted text-uppercase small mb-2">Loan Deductions</h6>
                                    {employeePayrollData.loans.map((l, i) => (
                                        <div key={i} className="d-flex justify-content-between align-items-center ps-3 small mb-1">
                                            <span>{l.title}</span>
                                            <span className="text-danger">-{(l.amount || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </>
                            )}<br />

                            <h6 className="fw-semibold text-muted text-uppercase small mb-2">Deductions</h6>

                            {/* Add deduction section - only show in edit mode */}
                            {!isViewMode && (
                                <div className="mb-3">
                                    <Row className="g-2">
                                        <Col md={6}>
                                            <Form.Select
                                                value={selectedDeduction}
                                                onChange={(e) => setSelectedDeduction(Number(e.target.value))}
                                            >
                                                <option value="">Select Deduction</option>
                                                {deductions.map((d) => (
                                                    <option key={d.id} value={d.id}>
                                                        {d.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Control
                                                type="number"
                                                value={deductionAmount}
                                                onChange={(e) => setDeductionAmount(parseFloat(e.target.value || 0))}
                                                placeholder="Amount"
                                                min="0"
                                                step="1"
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Button onClick={handleAddDeduction}>Add</Button>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Deductions list */}
                            {(employeePayrollData.deductions || []).map((d) => (
                                <div key={d.id} className="d-flex justify-content-between align-items-center mt-2 p-2 bg-light rounded">
                                    <span>
                                        {d.name} - {Number(d.amount || 0).toFixed(2)}
                                    </span>
                                    {!isViewMode && (
                                        <Button
                                            variant="link"
                                            className="text-danger p-0"
                                            onClick={() => removeDeduction(d.id)}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </div>
                            ))}


                            <div className="bg-danger bg-opacity-10 p-3 rounded border">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-bold text-danger">Total Deductions</span>
                                    <span className="fw-bold fs-5 text-danger">
                                        {payrollTotals.totalDeductions.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Net Salary */}
                        <div className="p-4 bg-gradient bg-white text-primary text-center">
                            <h6 className="text-primary-50 mb-1">NET PAYABLE AMOUNT</h6>
                            <h1 className="fw-bold mb-2"> {payrollTotals.netSalary.toFixed(2)}</h1>
                        </div>
                    </>
                )}
            </Offcanvas.Body>

            {/* Footer buttons - conditionally render based on mode */}
            <div className="offcanvas-footer p-3 border-top bg-light d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" onClick={onHide}>
                    {isViewMode ? 'Close' : 'Cancel'}
                </Button>
                {!isViewMode && (
                    <Button variant="primary" onClick={handleSave}>Save Payroll</Button>
                )}
            </div>
        </Offcanvas>
    );
};
export default EmployeePayrollOffcanvas;
